// A form being filled in on this phone (round 42).
//
// While somebody fills a form in, THE PHONE'S RECORD IS THE SOURCE OF TRUTH
// and the sync engine (./sync.ts) pushes it to the server. With signal that
// push lands in a second or two, which is the autosave the page always had;
// with none, it waits. One path, not an "online form" and an "offline form":
// two paths would disagree the first time one of them changed.
//
// The record remembers which sync steps have LANDED, so a queue interrupted
// halfway (the signal went, the phone was pocketed) picks up where it
// stopped, and every step it resends is one the server answers idempotently.
import type {
  FormContent,
  LinkKind,
  SubmissionMeter,
  TemplateAsset,
} from '../formsApi';

export interface LocalFile {
  /** Named by the phone; the server takes it (round 42). */
  id: string;
  fieldKey: string;
  kind: 'photo' | 'signature';
  filename: string;
  /** On the server. Until then the bytes live in the blob store. */
  uploaded: boolean;
}

export interface LocalForm {
  /** The submission id, generated here and used by the server as-is. */
  id: string;
  /** `tenantId:userId`: forms are kept against the account that filled them. */
  owner: string;
  templateId: string;
  templateName: string;
  /** The version the questions below came from. */
  versionId: string;
  linkKind: LinkKind;
  jobId: string | null;
  resourceId: string | null;
  /** What it is about, for headings and lists: "JOB-41 · Kerb" or "EX-05". */
  aboutLabel: string | null;
  /** The machine as the picker (or the server's snapshot) showed it. */
  machine: Pick<
    TemplateAsset,
    'name' | 'identifier' | 'registrationNumber' | 'kind' | 'machineTypeName'
  > | null;
  content: FormContent;
  meters: SubmissionMeter[];
  /** "Pick from the app" dropdowns, by field key. */
  options: Record<string, { id: string; label: string }[]>;

  answers: Record<string, unknown>;
  files: LocalFile[];
  /** Uploaded files taken off on the phone, still to remove on the server. */
  removedFileIds: string[];

  /** The server has the draft. */
  created: boolean;
  /** Started with no signal: the start sends the pinned `versionId`. */
  startedOnPhone: boolean;
  /** Bumped on every edit to the answers. */
  revision: number;
  /** The revision the server last saved. Equal means nothing to send. */
  savedRevision: number;
  /**
   * When Submit was pressed HERE, or null. Set means the form is finished and
   * waiting to send; it becomes the submit's `deviceSubmittedAt`.
   */
  submitAt: string | null;
  /**
   * The server's refusal, in its own words, when it said no. The form goes
   * back to being a draft on the phone with this on it: never dropped.
   */
  problem: string | null;
  problems: string[];
  createdAt: string;
  updatedAt: string;
}

export function ownerKey(tenantId: string, userId: string): string {
  return `${tenantId}:${userId}`;
}

/** Everything the server already has; nothing left to send. */
export function isSynced(form: LocalForm): boolean {
  return (
    form.created &&
    form.revision === form.savedRevision &&
    form.files.every((f) => f.uploaded) &&
    form.removedFileIds.length === 0 &&
    form.submitAt === null
  );
}

/** Something on this phone the server does not have yet. */
export function hasUnsent(form: LocalForm): boolean {
  return !isSynced(form);
}

/** A fresh id. `crypto.randomUUID` is on every browser this app supports. */
export function newId(): string {
  return crypto.randomUUID();
}
